"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const reminderTypes = [
  { emoji: "💉", label: "Vaccinations", example: "Rabies booster due in 3 days", urgency: "urgent", color: "#DC2626", bg: "#FEF2F2" },
  { emoji: "🦠", label: "Flea & tick treatment", example: "Monthly NexGard — due Sunday", urgency: "normal", color: "#5EBFB3", bg: "#E8F7F6" },
  { emoji: "🏥", label: "Vet appointments", example: "Annual checkup · Mar 15, 10am", urgency: "normal", color: "#0369A1", bg: "#E0F2FE" },
  { emoji: "✂️", label: "Grooming sessions", example: "Booked at PawSpa · Mar 22", urgency: "normal", color: "#7C3AED", bg: "#EDE9FE" },
  { emoji: "💊", label: "Daily medications", example: "Joint supplement · every morning", urgency: "normal", color: "#D97706", bg: "#FEF3C7" },
  { emoji: "🦷", label: "Dental cleaning", example: "Professional clean · Apr 10", urgency: "upcoming", color: "#059669", bg: "#DCFCE7" },
];

export default function RemindersFeature() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up, .scroll-fade-left, .scroll-fade-right").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 80);
            });
          }
        });
      },
      { threshold: 0.12 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24"
      style={{ background: "linear-gradient(160deg, #0F2027 0%, #1E3A3A 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left: Copy */}
          <div className="flex flex-col gap-6 scroll-fade-left">
            <span
              className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full w-fit"
              style={{ background: "rgba(14,165,160,0.15)", color: "#5CCFCB" }}
            >
              <Icon name="BellIcon" size={12} />
              Smart Reminders
            </span>
            <h2 className="text-4xl sm:text-[42px] font-extrabold text-white tracking-tight leading-tight">
              Never miss a shot,
              <br />
              <span className="gradient-text">a pill, or an appointment.</span>
            </h2>
            <p className="text-[#8AA4A4] leading-relaxed text-lg">
              DogFob tracks every vaccine, medication, and appointment automatically. When something's due, you'll get a push notification — before it becomes an emergency.
            </p>
            <ul className="flex flex-col gap-3">
              {[
                "Push notifications to your phone",
                "Email reminders as backup",
                "Shared reminders for family members",
                "Syncs with your calendar app",
                "Partner businesses can send reminders too",
              ]?.map((item) => (
                <li key={item} className="flex items-center gap-3 text-[#8AA4A4] text-sm">
                  <Icon name="CheckCircleIcon" size={16} className="text-teal-400 flex-shrink-0" variant="solid" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Reminder cards */}
          <div className="scroll-fade-right flex flex-col gap-3">
            {reminderTypes?.map((r, idx) => (
              <div
                key={r?.label}
                className={`scroll-fade-up stagger-${Math.min(idx + 1, 5)} glass-card-dark rounded-2xl px-5 py-4 flex items-center gap-4 hover:border-teal-500/25 transition-all duration-200`}
              >
                <span className="text-xl flex-shrink-0">{r?.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#8AA4A4] font-medium uppercase tracking-wide mb-0.5">{r?.label}</p>
                  <p className="text-sm font-semibold text-white truncate">{r?.example}</p>
                </div>
                <span
                  className="text-[10px] font-bold px-2.5 py-1 rounded-full flex-shrink-0"
                  style={{ background: r?.bg, color: r?.color }}
                >
                  {r?.urgency === "urgent" ? "⚡ Soon" : r?.urgency === "upcoming" ? "Upcoming" : "Scheduled"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}